import React from "react";
import { useNavigate } from "react-router-dom";
import shortid from "shortid";
import { SelectList } from "web-ui";
import { TextArea, TextInput } from "web-ui/juicy";
import { LegacyButton as Button, LegacyToggle as Toggle } from "web-ui/legacy";

import {
  finalStatuses,
  getDuolingoTtsLanguage,
  getTtsHash,
} from "../../../api/duolingoTts";
import styles from "../../../styles/AudioGenerator/AudioGenerator.module.scss";
import { downloadData } from "../../../util/download";
import { UrlParameterNames } from "../../../util/urlParameters";

import type { CommonEntryScreenProps, RowData } from "./EntryScreen";
import {
  displayNamesMapping,
  doDuolingoTtsRequest,
  doTtsGenerationRequest,
  duolingoTtsSpeeds,
  persistDuolingoTtsRows,
} from "./EntryScreen";

/* eslint-disable react/jsx-no-literals */
export const SingleItemEntryScreen = ({
  isSubmitted,
  lastUpdated,
  shouldPollForStatus,
  targetService,
  voices,
  setIsSubmitted,
  setLastUpdated,
  setShouldPollForStatus,
  voiceParam,
}: CommonEntryScreenProps) => {
  const [generationData, setGenerationData] = React.useState<
    Record<string, RowData>
  >(
    Object.fromEntries(
      Object.keys(displayNamesMapping).map(key => [
        key,
        key === "speed"
          ? "normal"
          : key === "voice" && voiceParam
            ? voiceParam
            : undefined,
      ]),
    ),
  );
  const [currentStatus, setCurrentStatus] = React.useState("not_submitted");
  const isGenerated = currentStatus === "created";

  const navigate = useNavigate();

  // If voice param is passed and valid, pre-select it
  React.useEffect(() => {
    if (voiceParam && voices.includes(voiceParam) && !generationData.voice) {
      setGenerationData(prevData => ({
        ...prevData,
        voice: voiceParam,
      }));
    }
  }, [voiceParam, voices, generationData.voice]);

  const submitTtsRequest = React.useCallback(
    async (isFirstCall = false) => {
      const relevantFunction =
        targetService === "duolingo-tts"
          ? doDuolingoTtsRequest
          : doTtsGenerationRequest;
      const functionOptions =
        targetService === "duolingo-tts"
          ? { shouldSubmitNewRequest: isFirstCall }
          : {
              onGenerated: (mp3Data: Blob) => {
                downloadData(mp3Data, `${shortid.generate()}.mp3`);
              },
            };
      const newStatus = await relevantFunction(
        { ...generationData, status: currentStatus },
        functionOptions,
      );
      const updateTime = new Date();
      setLastUpdated(updateTime);
      setCurrentStatus(newStatus);
      if (finalStatuses.has(newStatus)) {
        setShouldPollForStatus(false);
        if (targetService === "duolingo-tts") {
          persistDuolingoTtsRows([
            { ...generationData, date: updateTime.toJSON(), status: newStatus },
          ]);
        }
      }
    },
    [
      currentStatus,
      generationData,
      setLastUpdated,
      setShouldPollForStatus,
      targetService,
    ],
  );

  React.useEffect(() => {
    if (shouldPollForStatus) {
      const interval = setInterval(() => {
        submitTtsRequest();
      }, 15 * 1000);

      return () => {
        clearInterval(interval);
      };
    }

    return () => 0;
  }, [submitTtsRequest, shouldPollForStatus]);

  const hasRequiredFields =
    generationData.text !== undefined &&
    generationData.voice !== undefined &&
    (targetService === "tts-generation-backend" ||
      generationData.speed !== undefined);

  return (
    <>
      <div>
        <div className={styles["horizontal"]}>
          <h2>Field</h2>
          <h2>Value</h2>
        </div>
        <div className={styles["horizontal"]}>
          <h3>{displayNamesMapping.voice}</h3>
          <SelectList
            disabled={isSubmitted}
            items={voices.map(voice => ({ text: voice }))}
            onChange={e =>
              setGenerationData({
                ...generationData,
                voice: voices[e.selectedIndices[0]],
              })
            }
            popoverPosition={{
              direction: "down",
              manualPositioning: true,
              style: {
                marginTop: 50,
                maxHeight: 300,
                position: "absolute",
              },
              zIndex: 100,
            }}
            selectedIndices={
              typeof generationData.voice === "string"
                ? [voices.indexOf(generationData.voice)]
                : []
            }
            showSearch={true}
            text={generationData.voice ?? "Voice name"}
          />
        </div>
        {targetService === "duolingo-tts" ? (
          <div className={styles["horizontal"]}>
            <h3>{displayNamesMapping.speed}</h3>
            <SelectList
              disabled={isSubmitted}
              items={duolingoTtsSpeeds.map(speed => ({ text: speed }))}
              onChange={e =>
                setGenerationData({
                  ...generationData,
                  speed: duolingoTtsSpeeds[e.selectedIndices[0]],
                })
              }
              popoverPosition={{
                direction: "down",
                manualPositioning: true,
                style: {
                  marginTop: 50,
                  maxHeight: 300,
                  position: "absolute",
                },
                zIndex: 100,
              }}
              selectedIndices={
                typeof generationData.speed === "string"
                  ? [duolingoTtsSpeeds.indexOf(generationData.speed)]
                  : []
              }
              text={generationData.speed ?? "Speed"}
            />
          </div>
        ) : null}
        <div className={styles["horizontal"]}>
          <h3>{displayNamesMapping.text}</h3>
          <TextArea
            onChange={e =>
              setGenerationData({
                ...generationData,
                text: e.currentTarget.value || undefined,
              })
            }
            placeholder="Text to synthesize"
            rows={5}
            state={isSubmitted ? "disabled" : "enabled"}
            value={generationData.text?.toString()}
          />
        </div>
        <div className={styles["horizontal"]}>
          <h3>{displayNamesMapping.ssml}</h3>
          <TextArea
            onChange={e =>
              setGenerationData({
                ...generationData,
                ssml: e.currentTarget.value || undefined,
              })
            }
            placeholder="Speech Syntehsis Markup Language to fine-tune the synthesis. Leave blank for no SSML."
            rows={5}
            state={isSubmitted ? "disabled" : "enabled"}
            value={generationData.ssml?.toString()}
          />
        </div>
        {targetService === "tts-generation-backend" ? (
          <>
            <div className={styles["horizontal"]}>
              <h3>{displayNamesMapping.isRawSsml}</h3>
              <Toggle
                checked={
                  typeof generationData.isRawSsml === "boolean" &&
                  generationData.isRawSsml
                }
                disabled={isSubmitted}
                onChange={e =>
                  setGenerationData({
                    ...generationData,
                    isRawSsml: e.target.checked,
                  })
                }
              />
            </div>
            <div className={styles["horizontal"]}>
              <h3>{displayNamesMapping.prosody}</h3>
              <TextInput
                inputMode="decimal"
                min={0}
                onChange={e =>
                  setGenerationData({
                    ...generationData,
                    prosody: e.target.valueAsNumber || undefined,
                  })
                }
                placeholder="Relative prosody rate"
                state={isSubmitted ? "disabled" : "enabled"}
                type="number"
                value={generationData.prosody?.toString() ?? ""}
              />
            </div>
            <div className={styles["horizontal"]}>
              <h3>{displayNamesMapping.breakTime}</h3>
              <TextInput
                inputMode="numeric"
                min={0}
                onChange={e =>
                  setGenerationData({
                    ...generationData,
                    breakTime: e.target.valueAsNumber || undefined,
                  })
                }
                placeholder="Additional pause"
                state={isSubmitted ? "disabled" : "enabled"}
                type="number"
                value={generationData.breakTime?.toString() ?? ""}
              />
            </div>
          </>
        ) : null}
      </div>
      {isSubmitted ? (
        <>
          {currentStatus === "error" ? (
            <h3>An error has occurred and generation failed.</h3>
          ) : (
            <>
              {targetService === "duolingo-tts" ? (
                <>
                  {isGenerated ? (
                    <Button
                      onClick={async () => {
                        if (
                          typeof generationData.voice !== "string" ||
                          typeof generationData.text !== "string" ||
                          typeof generationData.speed !== "string" ||
                          (typeof generationData.ssml !== "undefined" &&
                            typeof generationData.ssml !== "string")
                        ) {
                          setCurrentStatus("error");
                          return;
                        }
                        const language = await getDuolingoTtsLanguage(
                          generationData.voice,
                        );
                        const hash = getTtsHash(
                          language,
                          generationData.voice,
                          generationData.speed,
                          generationData.text,
                          generationData.ssml,
                        );
                        navigate(`/?${UrlParameterNames.ttsHash}=${hash}`);
                      }}
                      role="link"
                      variant="solid"
                    >
                      Go to result
                    </Button>
                  ) : (
                    <p>
                      Generation is in progress! The status was last updated at{" "}
                      {lastUpdated.toTimeString()}. When finished, a button will
                      appear to go to the results.
                    </p>
                  )}
                </>
              ) : (
                <>
                  {isGenerated ? (
                    <p>
                      Your audio has been generated! Please check your downloads
                      folder for the MP3.
                    </p>
                  ) : (
                    <p>
                      Generation is in progress! The status was last updated at{" "}
                      {lastUpdated.toTimeString()}. Keep this page open and when
                      finished, your file will automatically download. Please be
                      patient when generating audio with World character voices
                      as we may need to activate some cloud resources before
                      generating the audio, which could take up to 5 minutes.
                    </p>
                  )}
                </>
              )}
              <Button
                onClick={() => {
                  setCurrentStatus("not_submitted");
                  setShouldPollForStatus(false);
                  setIsSubmitted(false);
                }}
                variant="stroke"
              >
                {isGenerated ? "Generate different audio" : "Edit submission"}
              </Button>
            </>
          )}
        </>
      ) : (
        <Button
          disabled={!hasRequiredFields}
          onClick={() => {
            setIsSubmitted(true);
            setShouldPollForStatus(true);
            submitTtsRequest(true);
          }}
          variant="solid"
        >
          Generate
        </Button>
      )}
    </>
  );
};
